/**
 * Distance Unit Conversion Utilities
 * Issue #1083: Standardize distance storage and display
 *
 * Design principles:
 * - Store in meters (canonical unit) in the database
 * - Convert at display time based on user preference
 * - User switching units never rewrites DB
 */

export type DistanceUnit = 'mi' | 'km';

/**
 * Elevation display unit. Not an independent preference (#3169):
 * it is derived from `distance_unit` — `mi` implies feet, `km` implies meters.
 * `elevation_gain_m` stays the canonical STORED unit (meters); conversion is
 * display-and-input only.
 */
export type ElevationUnit = 'ft' | 'm';

const METERS_PER_MILE = 1609.344;
const METERS_PER_KM = 1000;

// 1 meter = 3.28084 feet (#3169)
const FEET_PER_METER = 3.28084;

/**
 * Format meters for display in user's preferred unit
 * Always shows 1 decimal place
 *
 * @example
 * formatDistance(42195, 'km') // "42.2 km"
 * formatDistance(42195, 'mi') // "26.2 mi"
 */
export function formatDistance(
  meters: number | null | undefined,
  unit: DistanceUnit
): string {
  if (meters == null) return '';

  if (unit === 'mi') {
    const miles = meters / METERS_PER_MILE;
    return `${miles.toFixed(1)} mi`;
  } else {
    const km = meters / METERS_PER_KM;
    return `${km.toFixed(1)} km`;
  }
}

/**
 * Format meters for display without unit suffix
 * Useful for form inputs where unit is shown separately
 *
 * @example
 * formatDistanceValue(42195, 'km') // "42.2"
 * formatDistanceValue(42195, 'mi') // "26.2"
 */
export function formatDistanceValue(
  meters: number | null | undefined,
  unit: DistanceUnit
): string {
  if (meters == null) return '';

  if (unit === 'mi') {
    const miles = meters / METERS_PER_MILE;
    return miles.toFixed(1);
  } else {
    const km = meters / METERS_PER_KM;
    return km.toFixed(1);
  }
}

/**
 * Parse user input (in their preferred unit) to meters for storage
 * Rounds to nearest meter for clean integer storage
 *
 * @example
 * parseDistanceToMeters(26.2, 'mi') // 42165
 * parseDistanceToMeters(42.2, 'km') // 42200
 */
export function parseDistanceToMeters(
  value: number,
  unit: DistanceUnit
): number {
  return Math.round(
    unit === 'mi' ? value * METERS_PER_MILE : value * METERS_PER_KM
  );
}

/**
 * Convert meters to user's unit for form input fields
 * Returns number with 1 decimal precision
 *
 * @example
 * metersToDisplayValue(42195, 'km') // 42.2
 * metersToDisplayValue(42195, 'mi') // 26.2
 */
export function metersToDisplayValue(
  meters: number | null | undefined,
  unit: DistanceUnit
): number | '' {
  if (meters == null) return '';

  const value = unit === 'mi' ? meters / METERS_PER_MILE : meters / METERS_PER_KM;

  return Math.round(value * 10) / 10; // 1 decimal place
}

/**
 * Derive the elevation display unit from the user's distance unit (#3169).
 * `mi` → feet, `km` → meters. There is no independent elevation preference.
 *
 * @example
 * getElevationUnit('mi') // "ft"
 * getElevationUnit('km') // "m"
 */
export function getElevationUnit(distanceUnit: DistanceUnit): ElevationUnit {
  return distanceUnit === 'mi' ? 'ft' : 'm';
}

/**
 * Convert canonical meters to the user's elevation unit for a form input field.
 * Returns a whole number (elevation is not shown with decimals) or '' for empty.
 *
 * @example
 * metersToElevationValue(150, 'km') // 150   (m identity)
 * metersToElevationValue(150, 'mi') // 492   (feet)
 */
export function metersToElevationValue(
  meters: number | null | undefined,
  distanceUnit: DistanceUnit
): number | '' {
  if (meters == null) return '';
  const value = distanceUnit === 'mi' ? meters * FEET_PER_METER : meters;
  return Math.round(value);
}

/**
 * Parse user elevation input (in their derived unit) to canonical meters for
 * storage. Rounds to the nearest whole meter.
 *
 * @example
 * parseElevationToMeters(492, 'mi') // 150   (feet → meters)
 * parseElevationToMeters(150, 'km') // 150   (m identity)
 */
export function parseElevationToMeters(
  value: number,
  distanceUnit: DistanceUnit
): number {
  return Math.round(distanceUnit === 'mi' ? value / FEET_PER_METER : value);
}

/**
 * Format canonical meters as an elevation string in the user's derived unit (#3169).
 * A user set to `mi` sees feet; a user set to `km` sees meters — never a mismatch.
 *
 * @example
 * formatElevation(150, 'km') // "150 m"
 * formatElevation(150, 'mi') // "492 ft"
 */
export function formatElevation(
  meters: number | null | undefined,
  distanceUnit: DistanceUnit
): string {
  if (meters == null) return '';
  const value = metersToElevationValue(meters, distanceUnit);
  return `${value} ${getElevationUnit(distanceUnit)}`;
}

/**
 * Get the label for a distance unit
 *
 * @example
 * getUnitLabel('mi') // "Miles"
 * getUnitLabel('km') // "Kilometers"
 */
export function getUnitLabel(unit: DistanceUnit): string {
  return unit === 'mi' ? 'Miles' : 'Kilometers';
}

/**
 * Get the short label for a distance unit
 *
 * @example
 * getUnitShortLabel('mi') // "mi"
 * getUnitShortLabel('km') // "km"
 */
export function getUnitShortLabel(unit: DistanceUnit): string {
  return unit;
}
